import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const th = "py-2 pr-4 text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-1 font-serif text-3xl text-primary">{value}</p>
  </div>
);

const Breakdown = ({ title, items, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{title}</p>
    {!items.length ? <p className="mt-2 text-sm text-muted-foreground">Nothing yet.</p> : (
      <ul className="mt-2 space-y-2">{items.map((x) => {
        const max = items[0].amount || 1;
        return (
          <li key={x.name} className="text-sm">
            <div className="flex justify-between"><span>{x.name}</span><b>{gbp(x.amount)}</b></div>
            <div className="mt-1 h-1.5 rounded-full bg-border"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(x.amount / max) * 100}%` }} /></div>
          </li>
        );
      })}</ul>
    )}
  </div>
);

const Rows = ({ rows, testId, onRefund }) => (
  <div className="mt-3 overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead><tr><th className={th}>Date</th><th className={th}>Student</th><th className={th}>For</th><th className={th}>Method</th><th className={th}>Amount</th>{onRefund && <th className={th} />}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t border-border">
          <td className="py-2 pr-4">{r.date}</td><td className="pr-4">{r.student || r.email}</td><td className="pr-4">{r.item}{r.refund_note ? <span className="block text-xs text-muted-foreground">Refund: {r.refund_note}</span> : null}</td>
          <td className="pr-4 text-muted-foreground">{r.method}</td><td className="font-semibold">{gbp(r.amount)}</td>
          {onRefund && <td className="pl-2 text-right"><button onClick={() => onRefund(r)} data-testid={`payments-refund-${i}`} className="rounded-full border border-border px-3 py-1 text-xs hover:border-red-400 hover:text-red-500">Mark refunded</button></td>}
        </tr>))}</tbody>
    </table>
  </div>
);

const YearTable = ({ year, pick }) => (
  <section className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="payments-year">
    <h2 className="font-serif text-2xl">Last 12 months</h2>
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr><th className={th}>Month</th><th className={th}>Card</th><th className={th}>Bank transfer</th><th className={th}>Total</th><th className={th}>Payments</th></tr></thead>
        <tbody>{year.months.map((m) => (
          <tr key={m.month} className="cursor-pointer border-t border-border hover:bg-secondary/50" onClick={() => pick(m.month)} data-testid={`payments-year-${m.month}`}>
            <td className="py-2 pr-4 text-primary underline-offset-2 hover:underline">{monthName(m.month)}</td><td className="pr-4">{gbp(m.card)}</td>
            <td className="pr-4">{gbp(m.bank)}</td><td className="pr-4 font-semibold">{gbp(m.total)}</td><td>{m.count}{m.pending ? ` (+${m.pending} pending)` : ""}</td>
          </tr>))}
          <tr className="border-t-2 border-border font-semibold"><td className="py-2 pr-4">12-month total</td><td className="pr-4">{gbp(year.card)}</td><td className="pr-4">{gbp(year.bank)}</td><td className="pr-4 text-primary" data-testid="payments-year-total">{gbp(year.total)}</td><td /></tr>
        </tbody>
      </table>
    </div>
  </section>
);

// Admin: card and bank transfer income by month, last 12 months, with CSV and PDF downloads.
export default function AdminPayments() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/payments/year").then(({ data: d }) => setYear(d)).catch(() => {}); }, [tick]);
  useEffect(() => { setData(null); api.get("/admin/payments/summary", { params: { month } }).then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load payments")); }, [month, tick]);
  const refund = async (r) => {
    const note = window.prompt(`Mark ${gbp(r.amount)} from ${r.student || r.email} as refunded?\nThis removes it from the totals and locks any course it paid for.\nRemember to refund the money in Stripe or your bank.\n\nOptional note:`, "");
    if (note === null) return;
    try { const { data: d } = await api.post("/admin/payments/refund", { id: r.id, note }); toast.success(`Marked refunded${d.courses_removed ? " · course access removed" : ""}`); setTick((x) => x + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not mark refunded"); }
  };
  const download = async (format) => {
    try {
      const res = await api.get("/admin/payments/export", { params: { month, format }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `jt-hub-payments-${month}.${format}`; a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="payments-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl">Payment summary</h1>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <input type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="payments-month"
            className="rounded-full border border-border bg-background px-4 py-2 text-sm focus:border-primary focus:outline-none" />
          <button onClick={() => download("csv")} data-testid="payments-csv" className="inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"><Download size={14} /> Spreadsheet (CSV)</button>
          <button onClick={() => download("pdf")} data-testid="payments-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"><FileText size={14} /> Printable PDF</button>
        </div>
        {!data ? <p className="mt-6">Loading…</p> : (
          <>
            <div className="mt-6 grid gap-3 sm:grid-cols-4">
              <Stat label="Card" value={gbp(data.card)} testId="payments-card-total" />
              <Stat label="Bank transfer" value={gbp(data.bank)} testId="payments-bank-total" />
              <Stat label={`Total paid · ${monthName(month)}`} value={gbp(data.total)} testId="payments-total" />
              <Stat label="Payments" value={data.count} testId="payments-count" />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Breakdown title="By instrument" items={data.by_instrument} testId="payments-by-instrument" />
              <Breakdown title="By product" items={data.by_product} testId="payments-by-product" />
            </div>
            {data.payments.length ? <Rows rows={data.payments} testId="payments-rows" onRefund={refund} /> : <p className="mt-6 text-muted-foreground" data-testid="payments-empty">No paid payments in {monthName(month)}.</p>}
            {data.refunded.length > 0 && (
              <section className="mt-8" data-testid="payments-refunded">
                <h2 className="font-serif text-xl">Refunded <span className="text-sm text-muted-foreground">({gbp(data.refunded_total)}, not counted in totals)</span></h2>
                <Rows rows={data.refunded} testId="payments-refunded-rows" />
              </section>
            )}
            {data.pending.length > 0 && (
              <section className="mt-8" data-testid="payments-pending">
                <h2 className="font-serif text-xl">Pending bank transfers <span className="text-sm text-muted-foreground">({gbp(data.pending_total)}, not counted in totals)</span></h2>
                <Rows rows={data.pending} testId="payments-pending-rows" />
              </section>
            )}
          </>
        )}
        {year && <YearTable year={year} pick={(m) => { setMonth(m); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
      </main>
    </div>
  );
}
